/**
 * Exposure Limit list — Access `frmReportExposureMain` (Detail subform
 * `frmReportExposureQryLimit` over public_v_report_exposure_limit).
 *
 * Toolbar (Access FormHeader, by Left): Edit Limit → ExposureLimitEditDialog
 * (row-required, by code) · Choose Spot Month → ChooseSpotMonthDialog ·
 * Admin - Approve → ApproveExposureDialog (visible to approver accounts only —
 * Access btnApprove.Visible from tmp_add_information allowlist).
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { type CSSProperties, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import BrandState from '../../../components/BrandState'
import ResizeHandle from '../../../components/ResizeHandle'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../../components/dataList'
import { usePagePermission } from '../../../hooks/usePagePermission'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { useColumnWidths } from '../../../hooks/useColumnWidths'
import { useIsMobile } from '../../../hooks/useResponsive'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../../theme/responsive'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { titleCaseName } from '@/utils/text'
import { FuturesColumnsMenu } from '../../futures/FuturesColumnsMenu'
import {
  useGetExposureCanApproveQuery,
  useGetExposureLimitsQuery,
  type ExposureLimit,
} from '../../../store/api/dashboardApi'
import ApproveExposureDialog from './ApproveExposureDialog'
import ChooseSpotMonthDialog from './ChooseSpotMonthDialog'
import ExposureLimitEditDialog from './ExposureLimitEditDialog'

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: keyof ExposureLimit
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  /** Long text → wrap + row auto-grows (gold list `/sales/contract/list`). */
  wrap?: boolean
  /** Default width (px) when not yet resized — needed for wrap to have a bound. */
  width?: number
}

const ALL_ROWS_PAGE_SIZE = 1_000_000
const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const FROZEN_KEYS: ReadonlySet<string> = new Set(['code'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { code: 110 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { code: 120 }

/** Column order + captions — Access `frmReportExposureQryLimit` datasheet. */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'code', label: 'CP Code' },
  { key: 'name', label: 'Name', wrap: true, width: 220 },
  { key: 'shortName', label: 'Short Name', wrap: true, width: 200 },
  { key: 'limitDate', label: 'Limit Date (New)', kind: 'date' },
  { key: 'quantityLimitMt', label: 'Exposure Limit MT', kind: 'number', align: 'right' },
  { key: 'amountLimitUsd', label: 'Exposure Limit USD', kind: 'number', align: 'right' },
  { key: 'approvedTxt', label: 'Limit Status', filter: 'checkboxes' },
  { key: 'limitNote', label: 'Note', wrap: true, width: 220 },
  { key: 'dateUpdated', label: 'Updated Date' },
  { key: 'personUpdated', label: 'Updated Person' },
]

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: ExposureLimit[], key: keyof ExposureLimit): number | null => {
  let total = 0
  let hasValue = false
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || Number.isNaN(value)) continue
    total += value
    hasValue = true
  }
  return hasValue ? total : null
}

export default function ExposureLimitView() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const {
    selectedId: selectedCode,
    setSelectedId: setSelectedCode,
    toggleSelect: toggleSelectCode,
  } = useRowSelection<string>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof ExposureLimit> | null>(null)
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: keyof ExposureLimit; position: ColumnFilterAnchorPosition } | null>(null)
  const closeColPopover = () => setColPopover(null)
  const [editOpen, setEditOpen] = useState(false)
  const [spotOpen, setSpotOpen] = useState(false)
  const [approveOpen, setApproveOpen] = useState(false)
  const { getWidth, startResize } = useColumnWidths('report.exposure-limit')

  const columnsCtrl = usePreferenceColumns({
    scope: 'report_input',
    prefKey: 'columns',
    subKey: 'limit',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'report.exposure-limit',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const { data, isLoading, isFetching, error } = useGetExposureLimitsQuery(
    { page: 1, pageSize: ALL_ROWS_PAGE_SIZE },
    { refetchOnMountOrArgChange: true },
  )
  const { data: canApproveData } = useGetExposureCanApproveQuery()
  const canApprove = canApproveData?.canApprove ?? false

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) => rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) && rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const selectedRow = useMemo(
    () => baseRows.find((row) => row.code === selectedCode) ?? null,
    [baseRows, selectedCode],
  )
  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number')
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )
  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const minTableWidth = Math.max(1100, visibleColumns.length * 96)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const toggleSort = (key: keyof ExposureLimit) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const openEdit = () => {
    if (!canEdit) return
    if (!selectedRow) {
      snack.error('Please choose a record!')
      return
    }
    setEditOpen(true)
  }

  const cellValue = (row: ExposureLimit, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    if (col.key === 'name' || col.key === 'shortName') return titleCaseName(val)
    return String(val)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={openEdit}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit Limit"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={() => setSpotOpen(true)}
              startIcon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
              title="Choose Spot Month"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Spot Month</Box>
            </Button>
            {canApprove && (
              <Button
                variant="outlined"
                size="small"
                color="primary"
                sx={actionToolbarBtnSx}
                disabled={!canEdit}
                onClick={() => setApproveOpen(true)}
                startIcon={<VerifiedOutlinedIcon sx={{ fontSize: 16 }} />}
                title="Admin - Approve"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Approve</Box>
              </Button>
            )}
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => filterPopover.open('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, minHeight: 30, py: 0.125 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="CP code, name, status..."
              label="Search exposure limits"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        {/* Reload (refetch): progress strip + mờ bảng cho tới khi API trả data
            (mirror PnLCard) — trước đây chỉ có spinner 15px nên nhìn như không reload. */}
        <Box sx={{ height: 2 }}>{isFetching && !isLoading && <LinearProgress sx={{ height: 2, borderRadius: 1 }} />}</Box>
        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden', opacity: isFetching && !isLoading ? 0.55 : 1, pointerEvents: isFetching && !isLoading ? 'none' : 'auto', transition: 'opacity 0.15s' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Exposure Limits"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState title="No Exposure Limits found" description="Try adjusting the table filters above." />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) { headerStyle.position = 'sticky'; headerStyle.top = 0; headerStyle.left = 0; headerStyle.zIndex = 100 }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', ...(isFrozen ? {} : { position: 'relative' }) }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: ExposureLimit) =>
                visibleColumns.map((col) => {
                  const key = String(col.key)
                  const isFrozen = FROZEN_KEYS.has(key)
                  const val = cellValue(row, col)
                  const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  // WRAP: cột flag `wrap` HOẶC user đã resize → chữ dài xuống dòng,
                  // dòng tự cao (mirror `/sales/contract/list`).
                  const wrap = col.wrap || getWidth(key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={row != null && row.code === selectedCode}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (row?.code) toggleSelectCode(row.code)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>

      <ExposureLimitEditDialog
        open={editOpen}
        code={selectedRow?.code ?? null}
        name={selectedRow?.name ?? null}
        quantityLimitMt={selectedRow?.quantityLimitMt ?? null}
        amountLimitUsd={selectedRow?.amountLimitUsd ?? null}
        limitNote={selectedRow?.limitNote ?? null}
        onClose={() => setEditOpen(false)}
        onSaved={() => setSelectedCode(null)}
      />
      <ChooseSpotMonthDialog open={spotOpen} onClose={() => setSpotOpen(false)} onSaved={() => {}} />
      <ApproveExposureDialog open={approveOpen} onClose={() => setApproveOpen(false)} onSaved={() => setSelectedCode(null)} />
      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
